// Drag mode, ported from the POC (bymoses/better-web-kanban index.html, pointer drag 9ca8dea..0e54098): pointer
// events, CSS first. Touch = hold .35s (a CSS animation is the timer), mouse = 4px of movement. The lifted card is a
// copy in a top-layer popover that follows the finger by translate; zoom-out, grayscale, the gap (translates) and
// the shared dashed #slot are CSS under body.dragging (book.css). JS reads pointer coordinates, writes --x/--y/--my
// and toggles classes. Edge auto-scroll on both axes. On letting go the copy glides into its slot while the board
// scales back, then the card is shown under it; Escape, pointercancel or letting go outside the columns send it home.
// The drop never rebuilds the board: `onDrop` updates the store inside flushSync with transitions off, so React
// moves the keyed card in the same frame the gap closes; the store writes status + rank and rolls back on error.
import { type RefObject, useEffect } from "react";
import { flushSync } from "react-dom";

/** Card `id` dropped into column `status`, right after card `after` (null = at the top). */
export type Drop = (id: string, status: string, after: string | null) => void;

const EDGE = 56;

type Press = { card: HTMLElement; id: number; x: number; y: number; mouse: boolean };
type Drag = { card: HTMLElement; id: number; copy: HTMLElement; dx: number; dy: number; x0: number; y0: number; moved: boolean };

export function useDrag(bookRef: RefObject<HTMLElement | null>, onDrop: RefObject<Drop>) {
	useEffect(() => {
		const book = bookRef.current;
		if (!book) return;
		const row = () => book.querySelector<HTMLElement>("#row")!;
		let dragged: HTMLElement | null = null;
		let gapList: HTMLElement | null = null;
		let gapRef: HTMLElement | null = null;
		let gapPx = 0;
		let settleTimer: ReturnType<typeof setTimeout> | undefined;

		const isCard = (k: Element) => k.classList.contains("card") && !k.classList.contains("lifted");
		const endOf = (list: HTMLElement) => {
			let k = list.lastElementChild as HTMLElement | null;
			while (k && !isCard(k)) k = k.previousElementSibling as HTMLElement | null;
			return k ? k.offsetTop + k.offsetHeight + 6 : 0;
		};
		const nextCard = (c: Element) => {
			let n = c.nextElementSibling;
			while (n && !isCard(n)) n = n.nextElementSibling;
			return n as HTMLElement | null;
		};

		// The slot: one shared dashed element, moved by translate. Moving it to another list is the one layout per
		// column change; a gap move inside a list costs none.
		const slot = document.createElement("div");
		slot.id = "slot";
		slot.dataset.t = "drag-slot";
		slot.setAttribute("aria-hidden", "true");
		const setGap = (list: HTMLElement | null, ref: HTMLElement | null) => {
			if (list === gapList && ref === gapRef) return;
			// read before writing: offsets come from layout, which a gap move never dirties
			const y = list ? (ref ? ref.offsetTop : endOf(list)) : 0;
			gapRef?.classList.remove("gap");
			if (gapList !== list) {
				slot.classList.remove("on");
				if (list) {
					list.append(slot);
					slot.classList.add("fresh");
					requestAnimationFrame(() => requestAnimationFrame(() => slot.classList.remove("fresh")));
				}
			}
			gapList = list;
			gapRef = ref;
			if (!list) return;
			ref?.classList.add("gap");
			slot.style.setProperty("--my", `${y}px`);
			slot.classList.add("on");
		};
		// apply a layout change without the slide (pick-up and drop must not move anything)
		const instant = (fn: () => void) => {
			document.body.classList.add("instant");
			fn();
			requestAnimationFrame(() => requestAnimationFrame(() => document.body.classList.remove("instant")));
		};

		// Press → hold → pick up. `press` lives from pointerdown until pick-up or until the press ends; `drag` from
		// pick-up until letting go.
		const layer = document.createElement("div");
		layer.id = "drag-layer";
		layer.popover = "manual";
		layer.dataset.t = "drag-layer";
		document.body.append(layer);
		let press: Press | null = null;
		let drag: Drag | null = null;
		let lastScroll = -1e9;
		let eatClick = false;
		let px = 0;
		let py = 0;
		let edgeLoop = 0;

		// a touch that stops a momentum scroll is not a press (nothing listens per frame: one timestamp)
		const onAnyScroll = (e: Event) => {
			lastScroll = e.timeStamp;
		};
		const onPointerDown = (e: PointerEvent) => {
			eatClick = false;
			const c = (e.target as Element).closest<HTMLElement>(".card");
			if (!c || drag || press || !e.isPrimary || e.button || e.timeStamp - lastScroll < 100) return;
			press = { card: c, id: e.pointerId, x: e.clientX, y: e.clientY, mouse: e.pointerType === "mouse" };
			if (!press.mouse) c.classList.add("pressing");
		};
		const onAnimationEnd = (e: AnimationEvent) => {
			if (e.animationName === "hold" && press && e.target === press.card) pickUp(press.x, press.y);
		};
		const endPress = () => {
			press?.card.classList.remove("pressing");
			press = null;
		};
		const onPointerMove = (e: PointerEvent) => {
			if (drag && e.pointerId === drag.id) return move(e.clientX, e.clientY);
			if (press?.mouse && e.pointerId === press.id && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 4) pickUp(press.x, press.y, e);
		};
		// up: drop into the gap (none = outside the columns = cancel)
		const onPointerUp = (e: PointerEvent) => {
			if (drag && e.pointerId === drag.id) release(false);
			else if (press && e.pointerId === press.id) endPress();
			disarm();
		};
		// a native scroll took the touch (moved during the hold), or the system did
		const onPointerCancel = (e: PointerEvent) => {
			if (drag && e.pointerId === drag.id) release(true);
			else if (press && e.pointerId === press.id) endPress();
			disarm();
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape" && drag) release(true);
		};
		const onBlur = () => {
			endPress();
			if (drag) release(true);
		};
		// While a card is up the page must not scroll under the finger. Registered up front and non-passive: whether
		// a touch scrolls is decided on its first move, too late to add a listener then.
		const onTouchMove = (e: TouchEvent) => {
			if (drag) e.preventDefault();
		};
		const onContextMenu = (e: Event) => {
			if (press || drag) e.preventDefault();
		};
		// The click that ends a drag (mouse up, or the tap iOS may still send) must not open the card. It comes in the
		// same task as the pointerup, or not at all (a touch drag sends none): disarm right after, or the next click
		// without a press in the board (keyboard, a tap outside it) would be eaten.
		const onClick = (e: MouseEvent) => {
			if (!eatClick) return;
			eatClick = false;
			e.stopImmediatePropagation();
			e.preventDefault();
		};
		const disarm = () => {
			if (eatClick && !drag)
				setTimeout(() => {
					eatClick = false;
				});
		};

		function pickUp(x: number, y: number, mv?: PointerEvent) {
			const c = press!.card;
			const id = press!.id;
			endPress();
			const r = c.getBoundingClientRect(); // one read, after the press-in is gone
			const copy = c.cloneNode(true) as HTMLElement;
			for (const a of ["data-id", "role", "tabindex", "aria-current"]) copy.removeAttribute(a);
			copy.classList.remove("gap", "pressing");
			copy.dataset.t = "drag-copy";
			copy.setAttribute("aria-hidden", "true");
			copy.style.width = `${r.width}px`;
			drag = { card: c, id, copy, dx: x - r.left, dy: y - r.top, x0: x, y0: y, moved: false };
			layer.replaceChildren(copy);
			layer.showPopover?.();
			px = mv?.clientX ?? x;
			py = mv?.clientY ?? y;
			follow(px, py);
			dragged = c;
			eatClick = true;
			row().style.setProperty("--h", `${c.offsetHeight}px`);
			gapPx = c.offsetHeight + 6;
			// shrink around the middle of what's on screen; at either end, around that edge so no gap opens
			const ox =
				book!.scrollLeft <= 1
					? 0
					: book!.scrollLeft + book!.clientWidth >= book!.scrollWidth - 1
						? book!.scrollWidth
						: book!.scrollLeft + book!.clientWidth / 2;
			row().style.setProperty("--ox", `${ox}px`);
			instant(() => {
				c.classList.add("lifted");
				setGap(c.parentElement, nextCard(c)); // card out, gap in its place
			});
			c.closest(".col")?.classList.add("over"); // its column keeps its colour
			document.body.classList.add("dragging");
		}
		function follow(x: number, y: number) {
			layer.style.setProperty("--x", `${x - drag!.dx}px`);
			layer.style.setProperty("--y", `${y - drag!.dy}px`);
		}
		function move(x: number, y: number) {
			px = x;
			py = y;
			follow(x, y);
			aim(x, y);
			// edges count only once the card has really moved (one picked up near an edge stays put)
			drag!.moved ||= Math.hypot(x - drag!.x0, y - drag!.y0) > 12;
			if (drag!.moved && !edgeLoop && edgeSpeed().some(Boolean)) edgeLoop = requestAnimationFrame(edgeScroll);
		}
		// Where would it land: the column under the finger, before the first card whose middle is below it (else at
		// the end). Positions come from layout (offsetTop: translates don't change it) plus the gap's shift for cards
		// at/after it: one rect read per move, no reading of animated values. Outside the columns there is no gap:
		// letting go there cancels.
		function aim(x: number, y: number) {
			const col = document.elementFromPoint(x, y)?.closest<HTMLElement>(".col");
			if (!col || !book!.contains(col)) {
				book!.querySelector(".over")?.classList.remove("over");
				return setGap(null, null);
			}
			if (!col.classList.contains("over")) {
				book!.querySelector(".over")?.classList.remove("over");
				col.classList.add("over");
			}
			const list = col.querySelector<HTMLElement>(".cards")!;
			const lr = list.getBoundingClientRect();
			const ly = ((y - lr.top) * list.offsetHeight) / lr.height; // finger in list px (undo the drag scale)
			let shift = 0;
			let ref: HTMLElement | null = null;
			for (const k of list.children as HTMLCollectionOf<HTMLElement>) {
				if (!isCard(k)) continue;
				if (list === gapList && k === gapRef) shift = gapPx;
				if (k.offsetTop + shift + k.offsetHeight / 2 > ly) {
					ref = k;
					break;
				}
			}
			setGap(list, ref);
		}
		// Edge auto-scroll, both axes: near a side the row scrolls, near the top or bottom the column under the finger
		// does, faster the deeper into the band. The frame loop runs only while inside a band.
		const edgeSpeed = () => {
			const w = innerWidth;
			const h = innerHeight;
			const top = h * 0.15;
			const bottom = h * 0.88;
			return [px < EDGE ? px - EDGE : px > w - EDGE ? px - (w - EDGE) : 0, py < top ? py - top : py > bottom ? py - bottom : 0];
		};
		function edgeScroll() {
			edgeLoop = 0;
			if (!drag) return;
			const [vx, vy] = edgeSpeed();
			const col = book!.querySelector<HTMLElement>(".over");
			let moved = false;
			if (vx) {
				const x = book!.scrollLeft;
				book!.scrollLeft = x + vx * 0.35;
				moved = book!.scrollLeft !== x;
			}
			if (vy && col) {
				const y = col.scrollTop;
				col.scrollTop = y + vy * 0.35;
				moved ||= col.scrollTop !== y;
			}
			if (!moved) return; // at the ends: stop
			aim(px, py);
			edgeLoop = requestAnimationFrame(edgeScroll);
		}

		const snapLeft = (el: HTMLElement) => {
			const cw = book!.clientWidth;
			const a = getComputedStyle(el).scrollSnapAlign;
			const x = a.includes("center") ? el.offsetLeft - (cw - el.offsetWidth) / 2 : a.includes("end") ? el.offsetLeft + el.offsetWidth - cw : el.offsetLeft;
			return Math.max(0, Math.min(x, book!.scrollWidth - cw));
		};
		const nearestPage = () => {
			const mid = book!.scrollLeft + book!.clientWidth / 2;
			const d = (s: HTMLElement) => Math.abs(s.offsetLeft + s.offsetWidth / 2 - mid);
			return [...(row().children as HTMLCollectionOf<HTMLElement>)].filter((s) => !s.classList.contains("fill")).reduce((a, s) => (d(s) < d(a) ? s : a));
		};

		// Let go. Drop: the store moves the card into the gap now (hidden, .arriving); cancel: it stays home. Either
		// way the copy glides to where the card will sit once the board has scaled back and the row has settled (one
		// rect read in the shrunk board, mapped through the zoom), and the card is shown when it arrives.
		function release(cancel: boolean) {
			const d = drag!;
			drag = null;
			cancelAnimationFrame(edgeLoop);
			edgeLoop = 0;
			if (cancel) setGap(null, null);
			const r0 = row();
			const s = Number.parseFloat(getComputedStyle(r0).scale) || 1;
			const ox = Number.parseFloat(r0.style.getPropertyValue("--ox")) || 0;
			const from = book!.scrollLeft;
			const { left, card } = endDrag(d.card);
			card.classList.add("arriving");
			const r = card.getBoundingClientRect();
			d.copy.classList.add("back");
			d.copy.style.width = `${r.width / s}px`; // the last page is wider: the copy takes the slot's width
			layer.style.setProperty("--x", `${ox + (r.left + from - ox) / s - left}px`);
			layer.style.setProperty("--y", `${r.top / s}px`);
			let done = false;
			const land = () => {
				if (done) return;
				done = true;
				card.classList.remove("arriving");
				if (!drag) {
					layer.replaceChildren();
					if (layer.matches(":popover-open")) layer.hidePopover?.();
				}
			};
			d.copy.addEventListener("transitionend", (e) => e.propertyName === "translate" && land());
			setTimeout(land, 600); // no transitionend if it never moved
		}
		// Exit: never rebuild the board here (that killed the scale-back and reloaded images). Dropped: the store
		// commits status + rank in the same frame (flushSync), React moves the keyed card into the gap's place;
		// cancelled: it never moved. The same #row scales back and the row glides to the column snap would pick;
		// snapping comes back only after that (body.settling keeps it off). Returns the scrollLeft it glides to and
		// the card node as it is now (a move across columns mounts a new one).
		function endDrag(card: HTMLElement) {
			const id = card.dataset.id!;
			const list = gapList;
			const target = list?.closest<HTMLElement>(".col") ?? null;
			// the card the gap sits after (cards other than the dragged one, in list order)
			const others = list ? [...(list.children as HTMLCollectionOf<HTMLElement>)].filter(isCard) : [];
			const at = gapRef ? others.indexOf(gapRef) : others.length;
			const after = others[at - 1]?.dataset.id ?? null;
			dragged = null;
			book!.querySelector(".over")?.classList.remove("over");
			instant(() => {
				setGap(null, null);
				slot.remove();
				card.classList.remove("lifted");
				if (target) flushSync(() => onDrop.current(id, target.dataset.col!, after));
			});
			document.body.classList.add("settling");
			document.body.classList.remove("dragging");
			let left = book!.scrollLeft;
			if (book!.scrollWidth > book!.clientWidth) {
				left = snapLeft(target ?? nearestPage());
				book!.scrollTo({ left, behavior: "smooth" });
			}
			clearTimeout(settleTimer);
			settleTimer = setTimeout(() => document.body.classList.remove("settling"), 480);
			return { left, card: book!.querySelector<HTMLElement>(`.card[data-id="${id}"]`) ?? card };
		}

		const opts = { capture: true, passive: true } as const;
		document.addEventListener("scroll", onAnyScroll, opts);
		book.addEventListener("pointerdown", onPointerDown);
		book.addEventListener("animationend", onAnimationEnd);
		document.addEventListener("pointermove", onPointerMove);
		document.addEventListener("pointerup", onPointerUp);
		document.addEventListener("pointercancel", onPointerCancel);
		document.addEventListener("keydown", onKey);
		addEventListener("blur", onBlur);
		document.addEventListener("touchmove", onTouchMove, { passive: false });
		document.addEventListener("contextmenu", onContextMenu);
		document.addEventListener("click", onClick, true);
		return () => {
			document.removeEventListener("scroll", onAnyScroll, opts);
			book.removeEventListener("pointerdown", onPointerDown);
			book.removeEventListener("animationend", onAnimationEnd);
			document.removeEventListener("pointermove", onPointerMove);
			document.removeEventListener("pointerup", onPointerUp);
			document.removeEventListener("pointercancel", onPointerCancel);
			document.removeEventListener("keydown", onKey);
			removeEventListener("blur", onBlur);
			document.removeEventListener("touchmove", onTouchMove);
			document.removeEventListener("contextmenu", onContextMenu);
			document.removeEventListener("click", onClick, true);
			cancelAnimationFrame(edgeLoop);
			clearTimeout(settleTimer);
			slot.remove();
			layer.remove();
		};
	}, [bookRef, onDrop]);
}
